import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { API_URL, formatDate } from '../lib/utils';
import { toast } from 'sonner';
import {
  Inbox,
  Send,
  Mail,
  MailOpen,
  User,
  ArrowLeft,
  Loader2,
  PenLine,
  Search,
  MessageSquare,
  Clock,
  Briefcase,
} from 'lucide-react';

const Messages = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('inbox');
  const [messages, setMessages] = useState([]);
  const [sentMessages, setSentMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [composeOpen, setComposeOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [composeData, setComposeData] = useState({
    recipient_id: '',
    recipient_name: '',
    subject: '',
    content: '',
    job_id: null,
  });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const [inboxRes, sentRes] = await Promise.all([
        axios.get(`${API_URL}/messages/inbox`, {
          headers: {  },
        }),
        axios.get(`${API_URL}/messages/sent`, {
          headers: {  },
        }),
      ]);
      setMessages(inboxRes.data.messages);
      setUnreadCount(inboxRes.data.unread);
      setSentMessages(sentRes.data.messages);
    } catch (error) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!composeData.recipient_id || !composeData.subject || !composeData.content) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setSending(true);
    try {
      await axios.post(
        `${API_URL}/messages`,
        {
          recipient_id: composeData.recipient_id,
          subject: composeData.subject,
          content: composeData.content,
          job_id: composeData.job_id,
        },
        { headers: {  } }
      );
      
      toast.success('Message sent!');
      setComposeOpen(false);
      setComposeData({ recipient_id: '', recipient_name: '', subject: '', content: '', job_id: null });
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleViewMessage = async (message) => {
    setSelectedMessage(message);
    
    // Mark as read if unread
    if (!message.is_read && message.recipient_id === user?.user_id) {
      try {
        await axios.put(
          `${API_URL}/messages/${message.message_id}/read`,
          {},
          { headers: {  } }
        );
        setMessages(prev => prev.map(m => 
          m.message_id === message.message_id ? { ...m, is_read: true } : m
        ));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Failed to mark message as read:', error);
      }
    }
  };

  const handleReply = (message) => {
    setComposeData({
      recipient_id: message.sender_id,
      recipient_name: message.sender_name,
      subject: `Re: ${message.subject}`,
      content: '',
      job_id: message.job_id,
    });
    setSelectedMessage(null);
    setComposeOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="text-gray-500 hover:text-primary">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="font-serif text-3xl font-bold text-gray-900">Messages</h1>
              {unreadCount > 0 && (
                <p className="text-sm text-secondary">{unreadCount} unread messages</p>
              )}
            </div>
          </div>
          <Button
            onClick={() => setComposeOpen(true)}
            className="bg-primary hover:bg-primary-hover text-white"
            data-testid="compose-btn"
          >
            <PenLine className="w-4 h-4 mr-2" />
            Compose
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-gray-100 p-1 mb-6">
            <TabsTrigger value="inbox" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              <Inbox className="w-4 h-4 mr-2" />
              Inbox ({messages.length})
            </TabsTrigger>
            <TabsTrigger value="sent" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              <Send className="w-4 h-4 mr-2" />
              Sent ({sentMessages.length})
            </TabsTrigger>
          </TabsList>

          {/* Inbox */}
          <TabsContent value="inbox">
            {messages.length > 0 ? (
              <div className="bg-white rounded-xl border border-gray-100 divide-y">
                {messages.map((message) => (
                  <div
                    key={message.message_id}
                    onClick={() => handleViewMessage(message)}
                    className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                      !message.is_read ? 'bg-primary/5' : ''
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        !message.is_read ? 'bg-primary/10' : 'bg-gray-100'
                      }`}>
                        {!message.is_read ? (
                          <Mail className="w-5 h-5 text-primary" />
                        ) : (
                          <MailOpen className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <p className={`font-medium ${!message.is_read ? 'text-gray-900' : 'text-gray-600'}`}>
                            {message.sender_name}
                          </p>
                          <span className="text-xs text-gray-500">{formatDate(message.created_at)}</span>
                        </div>
                        <p className={`text-sm truncate ${!message.is_read ? 'text-gray-900 font-medium' : 'text-gray-600'}`}>
                          {message.subject}
                        </p>
                        {message.job_title && (
                          <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
                            <Briefcase className="w-3 h-3" />
                            {message.job_title}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-100 text-center py-12">
                <Inbox className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="font-medium text-gray-900 mb-2">No messages yet</h3>
                <p className="text-gray-500">Your inbox is empty</p>
              </div>
            )}
          </TabsContent>

          {/* Sent */}
          <TabsContent value="sent">
            {sentMessages.length > 0 ? (
              <div className="bg-white rounded-xl border border-gray-100 divide-y">
                {sentMessages.map((message) => (
                  <div
                    key={message.message_id}
                    onClick={() => handleViewMessage(message)}
                    className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                        <Send className="w-5 h-5 text-gray-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-medium text-gray-600">To: {message.recipient_name}</p>
                          <span className="text-xs text-gray-500">{formatDate(message.created_at)}</span>
                        </div>
                        <p className="text-sm text-gray-600 truncate">{message.subject}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-100 text-center py-12">
                <Send className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="font-medium text-gray-900 mb-2">No sent messages</h3>
                <p className="text-gray-500">You haven't sent any messages yet</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* View Message Dialog */}
      <Dialog open={!!selectedMessage} onOpenChange={() => setSelectedMessage(null)}>
        <DialogContent className="sm:max-w-lg">
          {selectedMessage && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-xl">{selectedMessage.subject}</DialogTitle>
                <DialogDescription>
                  {selectedMessage.sender_id === user?.user_id ? (
                    <>To: {selectedMessage.recipient_name}</>
                  ) : (
                    <>From: {selectedMessage.sender_name}</>
                  )}
                  {' • '}{formatDate(selectedMessage.created_at)}
                </DialogDescription>
              </DialogHeader>

              <div className="py-4">
                {selectedMessage.job_title && (
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-4 pb-4 border-b">
                    <Briefcase className="w-4 h-4" />
                    Regarding: {selectedMessage.job_title}
                  </div>
                )}
                <p className="text-gray-700 whitespace-pre-wrap">{selectedMessage.content}</p>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setSelectedMessage(null)}>
                  Close
                </Button>
                {selectedMessage.sender_id !== user?.user_id && (
                  <Button
                    onClick={() => handleReply(selectedMessage)}
                    className="bg-primary hover:bg-primary-hover text-white"
                  >
                    Reply
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Compose Dialog */}
      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">New Message</DialogTitle>
            {composeData.recipient_name && (
              <DialogDescription>To: {composeData.recipient_name}</DialogDescription>
            )}
          </DialogHeader>

          <form onSubmit={handleSendMessage} className="space-y-4 py-4">
            {!composeData.recipient_name && (
              <div>
                <Label htmlFor="recipient">Recipient ID</Label>
                <Input
                  id="recipient"
                  value={composeData.recipient_id}
                  onChange={(e) => setComposeData({ ...composeData, recipient_id: e.target.value })}
                  placeholder="Enter recipient user ID"
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  You can find this on their profile or from your applicants list
                </p>
              </div>
            )}

            <div>
              <Label htmlFor="subject">Subject</Label>
              <Input
                id="subject"
                value={composeData.subject}
                onChange={(e) => setComposeData({ ...composeData, subject: e.target.value })}
                placeholder="Message subject"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="content">Message</Label>
              <Textarea
                id="content"
                value={composeData.content}
                onChange={(e) => setComposeData({ ...composeData, content: e.target.value })}
                placeholder="Write your message..."
                rows={6}
                className="mt-1"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setComposeOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={sending}
                className="bg-primary hover:bg-primary-hover text-white"
              >
                {sending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Messages;
